import { Fragment, useState, useMemo } from 'react'
import { BackboneUserModel, getBackboneWindow } from '@opallabs/brown-dwarf'
import { ThemeProvider } from '@opallabs/hooks'
import { Button, ButtonSize, Icon } from '@opallabs/lapidary'
import { matchContentPath } from '@opallabs/routing'
import {
  CollaborationTextEditor,
  CollaborationToken,
  getCollaborationToken,
} from '@opallabs/text-editor'
import { setTextEditorAssetData } from '../../../contentTranslator/fromBackbonePost/dataSetters'
import * as styles from './styles'
import { DocumentProps } from './types'

const ASSET_PICKER_ARGS = { destination: 'document' }

export const Document: React.FC<DocumentProps> = ({
  externalAccessToken,
  richTextDocumentId,
  isFullscreen,
  isPresenting,
  isSnapshot,
  readOnly,
  onPickAssets,
  onToggleFullscreen,
}) => {
  const isContentComposer = !isPresenting && !isSnapshot && !readOnly
  const [user] = useState<BackboneUserModel | undefined>(() =>
    getBackboneWindow().AppBase?.request<BackboneUserModel | undefined>(
      'users:get_current_user'
    )
  )

  const post = matchContentPath(location.pathname.replace('/legacy', ''))

  const getViewerToken = useMemo(() => {
    if (!richTextDocumentId) {
      return
    }

    return async () => {
      const { token } = await getCollaborationToken(
        richTextDocumentId,
        'readonly'
      )
      return token
    }
  }, [richTextDocumentId])

  const isPresentationViewer = isPresenting && !user
  const dynamicToken:
    | CollaborationToken
    | (() => Promise<CollaborationToken>)
    | undefined =
    externalAccessToken ?? (isPresentationViewer ? getViewerToken : undefined)

  if (!richTextDocumentId) {
    return null
  }

  return (
    <Fragment>
      {isContentComposer && (
        <div css={styles.expandEditorToggle(isFullscreen)}>
          <ThemeProvider theme="dark">
            <Button
              css={styles.expandEditorButton}
              hideButtonText
              icon={
                <Icon
                  ariaHidden
                  css={styles.expandEditorIcon(isFullscreen)}
                  glyph="gem-indent"
                />
              }
              name="expand toggle"
              onClick={() => onToggleFullscreen(!isFullscreen)}
              size={ButtonSize.Small}
              variant="tertiary"
            />
          </ThemeProvider>
        </div>
      )}
      <CollaborationTextEditor
        alwaysShowVersionHistoryControl={isContentComposer}
        alwaysShowControlBar={isContentComposer}
        autofocus={false}
        externalAccessToken={dynamicToken}
        css={styles.editorStyleOverrides(isFullscreen)}
        disableDoneButton={true}
        disableFullScreenAndCenterControls={true}
        disableReadMoreOverlay={true}
        disableViewModeSelector={!isContentComposer}
        documentId={richTextDocumentId}
        onPickAssets={async () => {
          const assetRefs = await onPickAssets(ASSET_PICKER_ARGS)
          return assetRefs.map(setTextEditorAssetData)
        }}
        onToggleFullScreen={onToggleFullscreen}
        readOnly={readOnly}
        resource={post}
        user={
          user && isContentComposer
            ? {
                uuid: user.attributes.uuid as string,
                name: user.attributes.full_name as string,
                type: 'user',
                legacyId: user.attributes.id!.toString(),
                asset: user.attributes.avatar_asset_reference
                  ? {
                      url:
                        user.attributes.avatar_asset_reference.attributes
                          .full_url || '',
                    }
                  : undefined,
              }
            : null
        }
        useVersioning={isContentComposer}
      />
    </Fragment>
  )
}
